import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { ArrowLeft } from "@phosphor-icons/react";

const LOGO_URL =
  "/vznlogo.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      if (res.user.role === "admin") navigate("/admin");
      else if (res.user.role === "designer") navigate("/designer");
      else navigate("/dashboard");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r border-[#262626] mesh-overlay">
        <img
          src="/login-bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-white/80 hover:text-white" data-testid="back-home">
            <ArrowLeft size={16} /> Back home
          </Link>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.4em] text-[#FF2D2D] mb-3">Welcome back</div>
            <h1 className="font-display text-7xl leading-[0.9] text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">Pick up<br />where you<br />left off.</h1>
          </div>
          <div className="text-xs text-white/70 uppercase tracking-[0.2em]">Vision Grafix — Client Portal</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
          <div className="lg:hidden mb-8">
            <Link to="/" className="inline-flex w-full">
              <img src={LOGO_URL} alt="Vision Grafix" className="w-full h-auto object-contain" />
            </Link>
          </div>
          <h2 className="font-display text-4xl mb-2 uppercase">Sign in</h2>
          <p className="text-sm text-[#a3a3a3] mb-8">Welcome back. Let's keep building.</p>

          {err && (
            <div className="mb-4 p-3 border border-[#ff2a00] bg-[#ff2a00]/10 text-sm text-[#ff8866]" data-testid="login-error">
              {err}
            </div>
          )}

          <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 text-white focus:border-[#FF2D2D] outline-none transition-colors"
            placeholder="you@example.com"
          />

          <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-5 mb-2 flex items-center justify-between">
            <span>Password</span>
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              className="text-[#FF2D2D] hover:underline normal-case tracking-normal text-xs"
            >
              Forgot?
            </Link>
          </label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
            className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 text-white focus:border-[#FF2D2D] outline-none transition-colors"
            placeholder="••••••••"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="btn-primary w-full mt-8 px-6 py-3 text-sm font-bold uppercase tracking-wider"
          >
            {loading ? "Signing in…" : "Sign in →"}
          </button>

          <div className="mt-6 text-sm text-[#a3a3a3]">
            New here?{" "}
            <Link to="/register" className="text-[#FF2D2D] hover:underline" data-testid="login-register-link">
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
